/*
 * Copyright © 2024 Himitsu Lab Limited. All Rights Reserved.
 */

import React from 'react'
import { faRecordVinyl, faStop } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAudioRecording, useControlBarAccessHooks } from 'livekit/livekitHooks'
import { ToolTip } from 'base/tooltip/tooltip'
import { useTranslation } from 'react-i18next'
import { useCustomRoomContext } from 'livekit/room/CustomRoomContext'

export interface AudioRecordingProps {
  isPipEnabled: boolean | undefined
}

export default function CustomAudioRecording(props: AudioRecordingProps) {
  const { isPipEnabled } = props
  const { meetingEndTime, noTracks, onMessage, setLocalAudioRecording } = useCustomRoomContext()
  const { isHostOrCoHostAvailableRoom } = useControlBarAccessHooks()
  const { t } = useTranslation()
  const [clicked, setClicked] = React.useState(false)
  const [isRecording, setIsRecording] = React.useState(false)

  const { startAudioRecording, stopAudioRecording } = useAudioRecording(meetingEndTime, (value) => {
    setIsRecording(value)
    setLocalAudioRecording(value)
  })

  React.useEffect(() => {
    if (clicked && !isRecording && noTracks) {
      onMessage?.(t('pleaseTurnOnYourMicToStartRecording'), 'info')
      setClicked(false)
    }
  }, [isRecording, clicked, noTracks, onMessage, t])

  if (!isHostOrCoHostAvailableRoom) {
    return null
  }

  return (
    <>
      <ToolTip tip={isRecording ? t('stopRecording') : t('startRecording')}>
        <button
          id="btn_audio_recording"
          onClick={() => {
            setClicked(true)
            isRecording ? stopAudioRecording() : startAudioRecording()
          }}
          className={
            !isPipEnabled
              ? 'py-3 px-4 rounded-full border font-medium bg-gray-200 text-gray-700 hover:bg-gray-50 transition-all text-sm'
              : ''
          }
        >
          {isRecording ? (
            <FontAwesomeIcon icon={faStop} className="text-gray-500" />
          ) : (
            <FontAwesomeIcon icon={faRecordVinyl} />
          )}
        </button>
      </ToolTip>
    </>
  )
}
